'use client'

// xoá sau khi chuyển bảng sang nam <=> nữ
import { Tabs } from '@/components/ui/tabs'
import { LocalStorage } from '@/lib/utils/local-storage'
import { __cProductService } from '@/services/client/__c.product.service'
import { SizeCalculationMaleParams, SizeMaleSuggestion } from '@/types/size'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useDebounceValue } from 'usehooks-ts'
import { useProductDetailSafe } from '../../context/product-detail-context'
import { SizeGuideMaleContent } from './size-guide-content'
import { SizeTableMaleContent } from './size-table-content'

// Tạo key cho cache
const getSizeCacheKey = (params: SizeCalculationMaleParams): string[] => [
  'size-suggestion',
  `${params.height}`,
  `${params.weight}`,
  params.body,
  params.sizechart,
]

export default function ProductInfoSizeMale() {
  const { product } = useProductDetailSafe()
  const queryClient = useQueryClient()

  // Initialize state with values from localStorage if available
  const initialData = LocalStorage.get<SizeCalculationMaleParams>('MALE_SIZE_DATA')

  const [height, setHeight] = useState<number>(initialData?.height ?? 155)
  const [weight, setWeight] = useState<number>(initialData?.weight ?? 40)
  const [bodyType, setBodyType] = useState<string>(initialData?.body ?? 'normal')
  const [activeTab, setActiveTab] = useState('guide')
  const [recommendedSize, setRecommendedSize] =
    useState<SizeMaleSuggestion | null>(null)

  const [debouncedHeight] = useDebounceValue(height, 300)
  const [debouncedWeight] = useDebounceValue(weight, 300)

  const sizeChart = product?.sizechart

  // Tạo params cho mutation
  const sizeParams = useMemo((): SizeCalculationMaleParams => {
    return {
      height: debouncedHeight,
      weight: debouncedWeight,
      body: bodyType,
      sizechart: sizeChart || '',
    }
  }, [debouncedHeight, debouncedWeight, bodyType, sizeChart])

  // Tạo cache key
  const cacheKey = useMemo(() => {
    return getSizeCacheKey(sizeParams)
  }, [sizeParams])

  // Save to localStorage whenever values change
  useEffect(() => {
    LocalStorage.set('MALE_SIZE_DATA', {
      height,
      weight,
      body: bodyType,
      sizechart: sizeChart || ''
    })
  }, [height, weight, bodyType, sizeChart])

  const { mutate: calculateSize, isPending } = useMutation({
    mutationFn: (params: SizeCalculationMaleParams) =>
      __cProductService.getSizeMaleSuggestion(params),
    onSuccess: (data) => {
      if (data) {
        setRecommendedSize(data)

        // Lưu kết quả vào cache
        queryClient.setQueryData(cacheKey, data)
      }
    },
  })

  // Kiểm tra cache trước khi tính toán
  useEffect(() => {
    if (debouncedHeight && debouncedWeight && bodyType && sizeChart) {
      // Tìm kiếm kết quả trong cache
      const cachedResult =
        queryClient.getQueryData<SizeMaleSuggestion>(cacheKey)

      if (cachedResult) {
        // Nếu có kết quả trong cache thì dùng luôn
        setRecommendedSize(cachedResult)
      } else {
        // Nếu không có trong cache thì tính toán
        calculateSize(sizeParams)
      }
    }
  }, [
    debouncedHeight,
    debouncedWeight,
    bodyType,
    sizeChart,
    calculateSize,
    queryClient,
    cacheKey,
    sizeParams,
  ])

  const tabOptions = [
    {
      label: 'Hướng dẫn chọn size',
      value: 'guide',
      content: (
        <SizeGuideMaleContent
          height={height}
          weight={weight}
          bodyType={bodyType}
          setHeight={setHeight}
          setWeight={setWeight}
          setBodyType={setBodyType}
          recommendedSize={recommendedSize}
          isLoading={isPending}
          sizeChart={sizeChart}
        />
      ),
    },
    {
      label: 'Bảng size',
      value: 'table',
      content: <SizeTableMaleContent sizeChart={sizeChart} />,
    },
  ]

  return (
    <Tabs
      options={tabOptions}
      onChange={setActiveTab}
      defaultValue={activeTab}
      variant='neutral'
      className='h-fit border-b border-b-neutral-200 pb-0'
      triggerClassName='flex-1 border-b-[3px] font-semibold font-sans text-sm leading-4.5 px-4'
    />
  )
}
